import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initBrandTheme } from "@/components/ThemeToggle";
import { initBrandProfile } from "@/lib/brandProfile";

// Apply persisted brand theme as early as possible so the page never flashes
// the wrong palette before React paints (Toyota-red ↔ SSP-green demo toggle).
initBrandTheme();
// White-label brand profile (RRG / Toyota) for CEO-pitch demos. Reads
// ?brand=rrg|toyota|default URL param OR localStorage, applies the matching
// data-theme attribute, and swaps the wordmark via BrandMark.
initBrandProfile();

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import JobEditorPage from "@/pages/JobEditorPage";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import PublicJobPage from "@/pages/PublicJobPage";
import CatalogPage from "@/pages/CatalogPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import CinematicDemoPage from "@/pages/CinematicDemoPage";
import WalkthroughPage from "@/pages/WalkthroughPage";
import TechStandardsPage from "@/pages/TechStandardsPage";
import TeamPage from "@/pages/TeamPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import PrintableDiaryPage from "@/pages/PrintableDiaryPage";
import BundlesManagerPage from "@/pages/BundlesManagerPage";
import DataHomePage from "@/pages/DataHomePage";
import AdminPage from "@/pages/AdminPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import HelpPage from "@/pages/HelpPage";
import PreCallsPage from "@/pages/PreCallsPage";
import PublicQuestionnairePage from "@/pages/PublicQuestionnairePage";
import PreCallDetailsPage from "@/pages/PreCallDetailsPage";
import VehicleVisualsDemoPage from "@/pages/VehicleVisualsDemoPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import BillingOnboardingPage from "@/pages/BillingOnboardingPage";
import GroupDashboardPage from "@/pages/GroupDashboardPage";
import GroupDealerDrilldownPage from "@/pages/GroupDealerDrilldownPage";
import ServicePlanProspectsPage from "@/pages/ServicePlanProspectsPage";
import CustomerProfilePage from "@/pages/CustomerProfilePage";
import LaunchDemoPage from "@/pages/LaunchDemoPage";
import ProspectsPage from "@/pages/ProspectsPage";
import SmsUsagePage from "@/pages/SmsUsagePage";
import AiVhcTesterPage from "@/pages/AiVhcTesterPage";
import RoiCalculatorPage from "@/pages/RoiCalculatorPage";
import PilotPage from "@/pages/PilotPage";
import TheProcessPage from "@/pages/TheProcessPage";
import FilmPage from "@/pages/FilmPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import WorkshopWallPage from "@/pages/WorkshopWallPage";
import AdvisorDeskPage from "@/pages/AdvisorDeskPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ArchivedJobsPage from "@/pages/ArchivedJobsPage";
import InstallPrompt from "@/components/InstallPrompt";
import MagicBrandSwitch from "@/components/MagicBrandSwitch";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Boardroom demo accounts (Arran, Dave, Clive) land on the standard
  // ServiceSell PRO dark theme. Steve triggers the Magic Brand Switch
  // live during the pitch to demo the transform: default -> Toyota -> RRG.
  // Arran gave written permission (Mar 2026) to use RRG data + name.
  // Magic brand-switch shows for Steve (platform_owner) and RRG boardroom
  // demo accounts. Real customers never see it. Hidden on the Advisor Desk
  // so a customer holding the iPad in Handover mode can't tap it.
  const showSwitch = (user.platform_owner || user.is_demo_user) && pathname !== "/advisor-desk";
  return (
    <>
      {showSwitch && <MagicBrandSwitch />}
      {children}
    </>
  );
}

/**
 * DirectorOnly — gates a route to dealer principals / group directors only.
 * Allowed: workshop owners (`role_in_workshop === "owner"`) AND platform owners.
 * Blocked: workshop advisors (managers / floor staff) get a friendly redirect.
 * Unauthenticated: redirected to login with a return path.
 */
function DirectorOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login?return=/roi-calculator&reason=directors-only" replace />;
  const isDirector = user.platform_owner || user.role_in_workshop === "owner";
  if (!isDirector) {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-6" data-testid="roi-directors-only-gate">
        <div className="max-w-md w-full rounded-lg border border-warning/30 bg-warning/[0.06] p-6 text-center space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-warning">Directors only</div>
          <h1 className="font-display font-bold text-2xl">ROI Calculator is restricted</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This page is reserved for dealer principals and group directors. If you need the ROI numbers
            for your sites, please ask your director to access it for you, or have them grant you owner
            permissions on your workshop.
          </p>
          <a href="/dashboard" className="btn-primary inline-flex" data-testid="roi-gate-back-dashboard">
            Back to dashboard
          </a>
        </div>
      </div>
    );
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Toaster — top-right with header-clearing offset so the welcome
            toast no longer lands on top of the dashboard header buttons.
            `top-right` keeps toasts well away from the nav while still being
            in the user's reading direction. */}
        <Toaster
          position="top-right"
          richColors
          offset="80px"
          toastOptions={{ classNames: { toast: "shadow-2xl" } }}
        />
        <InstallPrompt />
        <AppErrorBoundary>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/roi-calculator" element={<DirectorOnly><RoiCalculatorPage /></DirectorOnly>} />
          <Route path="/pilot" element={<PilotPage />} />
          <Route path="/the-process" element={<TheProcessPage />} />
          <Route path="/film" element={<FilmPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/catalog" element={<Protected><CatalogPage /></Protected>} />
          <Route path="/how-it-works" element={<Protected><HowItWorksPage /></Protected>} />
          <Route path="/help" element={<Protected><HelpPage /></Protected>} />
          <Route path="/analytics" element={<Protected><AnalyticsPage /></Protected>} />
          <Route path="/sms-usage" element={<Protected><SmsUsagePage /></Protected>} />
          <Route path="/pre-calls" element={<Protected><PreCallsPage /></Protected>} />
          <Route path="/settings/bundles" element={<Protected><BundlesManagerPage /></Protected>} />
          <Route path="/settings/data-home" element={<Protected><DataHomePage /></Protected>} />
          <Route path="/service-plan-prospects" element={<Protected><ServicePlanProspectsPage /></Protected>} />
          <Route path="/jobs/:id" element={<Protected><AppErrorBoundary><JobEditorPage /></AppErrorBoundary></Protected>} />
          <Route path="/j/:token" element={<PublicJobPage />} />
          <Route path="/q/:token" element={<PublicQuestionnairePage />} />
          <Route path="/precall/:token" element={<PreCallDetailsPage />} />
          <Route path="/demo/vehicle-visuals" element={<VehicleVisualsDemoPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/billing/onboarding" element={<Protected><BillingOnboardingPage /></Protected>} />
          <Route path="/group" element={<Protected><GroupDashboardPage /></Protected>} />
          <Route path="/group/dealers/:workshopId" element={<Protected><GroupDealerDrilldownPage /></Protected>} />
          <Route path="/customers/:reg" element={<Protected><CustomerProfilePage /></Protected>} />
          <Route path="/watch" element={<CinematicDemoPage />} />
          <Route path="/demo" element={<LaunchDemoPage />} />
          <Route path="/walkthrough" element={<WalkthroughPage />} />
          <Route path="/tech-standards" element={<TechStandardsPage />} />
          <Route path="/team" element={<Protected><TeamPage /></Protected>} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/diary/print" element={<Protected><PrintableDiaryPage /></Protected>} />
          <Route path="/workshop-wall" element={<Protected><WorkshopWallPage /></Protected>} />
          <Route path="/advisor-desk" element={<Protected><AdvisorDeskPage /></Protected>} />
          <Route path="/reports/archive" element={<Protected><ArchivedJobsPage /></Protected>} />
          <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
          <Route path="/prospects" element={<Protected><ProspectsPage /></Protected>} />
          <Route path="/accounts" element={<Protected><AdminPage /></Protected>} />
          <Route path="/accounts/ai-vhc-tester" element={<Protected><AiVhcTesterPage /></Protected>} />
          <Route path="/watch-classic" element={<HowItWorksPage publicMode={true} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AppErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
